/**
 * The metric explorer: any rollup key, one grain at a time.
 *
 * Three guards that make it usable rather than merely powerful:
 *   - **One grain.** Mixing grains produces a number that means nothing, so grain is a single
 *     choice, not a set of checkboxes. The rollup stores each grain as its own row, so this
 *     mirrors the data rather than constraining it arbitrarily.
 *   - **A spaghetti guard.** Past five keys on one axis a line chart stops helping, so the
 *     chart is suppressed and the table becomes the view — and it says why rather than
 *     silently truncating.
 *   - **The key list comes from the server.** An earlier version kept its own and offered keys
 *     nothing emits; on a dashboard, a key nobody writes is indistinguishable from a metric
 *     that has collapsed to zero.
 *
 * **Event health** is today's accepted/rejected count per event, tallied at ingestion (doc 43
 * §8). It carries counts only: rejection *reasons* stay server-log-only, because a client that
 * can read them is a client somebody tunes against. The threshold and the over-the-line verdict
 * both come from the server, so the footer quotes the setting in force rather than a constant.
 */
import { useState } from 'react';
import { Button, Card, Chip, Kicker, StatusChip } from '../components/primitives';
import { Select } from '../components/forms';
import { EmptyState, ErrorState, LoadingState } from '../components/feedback';
import { DataTable, type Column } from '../components/data';
import { Section, Sparkline } from '../components/kit';
import { errorMessage } from '../lib/api/client';
import {
  useEventHealth,
  useMetricKeys,
  useMetrics,
  type EventHealthResponse,
  type MetricPoint,
} from '../lib/api/hooks/analytics';
import { useCities } from '../lib/api/hooks/geo';
import { usePujas } from '../lib/api/hooks/catalogue';
import { WINDOWS, windowDates } from './Funnel';
import { resolveWindow, useWindowSearch } from '../app/urlState';

type Grain = 'overall' | 'city' | 'puja';

const GRAINS: readonly { key: Grain; label: string }[] = [
  { key: 'overall', label: 'Overall' },
  { key: 'city', label: 'Per city' },
  { key: 'puja', label: 'Per puja' },
];

/** Past this many series on one axis a line chart stops helping and starts hiding. */
export const MAX_CHARTED_KEYS = 5;

/** `funnel.puja_viewed` → `funnel.*` — the prefix a key belongs to, for grouping the picker. */
export function keyPrefix(key: string): string {
  return `${key.split('.')[0]}.*`;
}

/** Group the server's flat key list the way an operator reads it. */
export function groupKeys(all: readonly string[]): { prefix: string; keys: string[] }[] {
  const groups = new Map<string, string[]>();
  for (const key of all) {
    const prefix = keyPrefix(key);
    groups.set(prefix, [...(groups.get(prefix) ?? []), key]);
  }
  return [...groups.entries()].map(([prefix, keys]) => ({ prefix, keys }));
}

/** One row per date, one column per selected key — the table twin of the chart. */
export function toSeries(points: readonly MetricPoint[]): { days: string[]; byKey: Map<string, Map<string, number>> } {
  const days = [...new Set(points.map((p) => p.metric_date))].sort();
  const byKey = new Map<string, Map<string, number>>();
  for (const point of points) {
    const row = byKey.get(point.metric_key) ?? new Map<string, number>();
    // Summed rather than replaced: at a per-city grain a date carries one row per city, and the
    // series is the total across whichever rows the server returned for the filter in force.
    row.set(point.metric_date, (row.get(point.metric_date) ?? 0) + point.value);
    byKey.set(point.metric_key, row);
  }
  return { days, byKey };
}

const HEALTH_COLUMNS: Column[] = [
  { key: 'event', label: 'Event', mono: true },
  { key: 'acc', label: 'Accepted', align: 'right', mono: true },
  { key: 'rej', label: 'Rejected', align: 'right', mono: true },
  { key: 'rate', label: 'Rejected rate', align: 'right' },
  { key: 'status', label: '' },
];

function EventHealth({ health }: { health: EventHealthResponse }) {
  const line = health.threshold_percent;
  if (health.events.length === 0) {
    return (
      <EmptyState
        icon="activity"
        title="No events ingested today"
        body="Nothing has reached ingestion since midnight IST, accepted or rejected."
      />
    );
  }
  return (
    <DataTable
      density="compact"
      columns={HEALTH_COLUMNS}
      rows={health.events.map((e) => {
        const bad = e.above_threshold;
        return {
          id: e.event_name,
          event: e.event_name,
          acc: (
            <span className="tabular" style={{ display: 'block', textAlign: 'right' }}>
              {e.accepted.toLocaleString('en-IN')}
            </span>
          ),
          rej: (
            <span
              className="tabular"
              style={{ display: 'block', textAlign: 'right', color: bad ? 'var(--c-warn-text)' : 'var(--c-ink)' }}
            >
              {e.rejected.toLocaleString('en-IN')}
            </span>
          ),
          rate: (
            <span
              className="tabular"
              style={{ display: 'block', textAlign: 'right', color: bad ? 'var(--c-warn-text)' : 'var(--c-ink3)' }}
            >
              {e.rejected_percent.toFixed(1)}%
            </span>
          ),
          status: bad ? <StatusChip tone="warn" label={`above the ${line}% line`} /> : null,
        };
      })}
      footer={
        <>
          <span>
            Threshold is {line}% — exactly {line.toFixed(1)}% does not raise the banner
          </span>
          <span>Rejections are schema failures, not user errors</span>
        </>
      }
    />
  );
}

export function MetricExplorer() {
  const [selected, setSelected] = useState<string[]>([]);
  /* Window and dimension live in the URL (doc 05 §2); a pasted link names its grain by which id
     it carries. The key selection stays local — it is a working set, not a view to share. */
  const [search, setSearch] = useWindowSearch();
  const [grain, setGrain] = useState<Grain>(
    search.city_id !== undefined ? 'city' : search.puja_id !== undefined ? 'puja' : 'overall',
  );

  const catalogue = useMetricKeys();
  const health = useEventHealth();
  const cities = useCities({ page: 1, page_size: 100 });
  const pujas = usePujas({ page: 1, page_size: 100 });

  const cityItems = cities.data?.items ?? [];
  const pujaItems = pujas.data?.items ?? [];
  const cityId = grain === 'city' ? search.city_id : undefined;
  const pujaId = grain === 'puja' ? search.puja_id : undefined;
  /* The Selects take names; the URL and the API take ids. One list maps both ways. */
  const dimension =
    grain === 'city'
      ? (cityItems.find((c) => c.id === cityId)?.name ?? '')
      : (pujaItems.find((p) => p.id === pujaId)?.name_en ?? '');
  const setDimension = (v: string) =>
    grain === 'city'
      ? setSearch({ city_id: cityItems.find((c) => c.name === v)?.id })
      : setSearch({ puja_id: pujaItems.find((p) => p.name_en === v)?.id });

  const dates = resolveWindow(
    search,
    WINDOWS.map((w) => w.days),
    30,
    new Date(),
  );
  const metrics = useMetrics(
    {
      date_from: dates.from,
      date_to: dates.to,
      ...(cityId !== undefined ? { city_id: cityId } : {}),
      ...(pujaId !== undefined ? { puja_id: pujaId } : {}),
    },
    selected,
  );

  const toggle = (k: string) =>
    setSelected((cur) => (cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k]));

  const pickGrain = (g: Grain) => {
    setGrain(g);
    // The dimension belongs to the grain; carrying a city into the per-puja view would filter
    // by something the operator is no longer looking at.
    setSearch({ city_id: undefined, puja_id: undefined });
  };

  const tooMany = selected.length > MAX_CHARTED_KEYS;
  const points = metrics.data?.items ?? [];
  const { days: dayLabels, byKey } = toSeries(points);

  const columns: Column[] = [
    { key: 'day', label: 'Date' },
    ...selected.map((k) => ({
      key: k,
      label: k.replace(/^\w+\./, ''),
      align: 'right' as const,
      mono: true,
    })),
  ];

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 300px) minmax(0, 1fr)',
        gap: 'var(--sp-lg)',
        alignItems: 'start',
      }}
    >
      <div style={{ display: 'grid', gap: 'var(--sp-lg)', position: 'sticky', top: 'var(--sp-lg)' }}>
        <Section title="Metric keys" note={`${selected.length} selected · the list comes from the rollup`}>
          {catalogue.isPending && <LoadingState message="Reading the metric catalogue…" />}
          {catalogue.isError && (
            <ErrorState
              title="Could not read the metric catalogue"
              cause={errorMessage(catalogue.error)}
            />
          )}
          {catalogue.isSuccess && (
            <div style={{ display: 'grid', gap: 'var(--sp-md)' }}>
              {groupKeys(catalogue.data.keys).map((g) => (
                <div key={g.prefix} style={{ display: 'grid', gap: 6 }}>
                  <Kicker>{g.prefix}</Kicker>
                  {g.keys.map((k) => (
                    <label
                      key={k}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--sp-sm)',
                        padding: '5px var(--sp-sm)',
                        borderRadius: 'var(--r-tile)',
                        background: selected.includes(k) ? 'var(--c-saffron-50)' : 'transparent',
                        cursor: 'pointer',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selected.includes(k)}
                        onChange={() => toggle(k)}
                        style={{ width: 15, height: 15 }}
                      />
                      <span className="mono-num" style={{ fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink)' }}>
                        {k}
                      </span>
                    </label>
                  ))}
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section title="Grain" note="One at a time — mixing grains makes a number that means nothing">
          <div style={{ display: 'grid', gap: 'var(--sp-sm)', justifyItems: 'start' }}>
            {GRAINS.map((g) => (
              <Chip
                key={g.key}
                variant="filter"
                label={g.label}
                selected={grain === g.key}
                onClick={() => pickGrain(g.key)}
              />
            ))}
            {/* The grain is only half a choice without the dimension: "Per city" with no city
                named would send the same unfiltered request and relabel the answer, which is a
                control that lies rather than one that does nothing. */}
            {grain === 'city' && (
              <Select
                density="admin"
                placeholder="Which city"
                value={dimension}
                onChange={setDimension}
                options={(cities.data?.items ?? []).map((c) => c.name)}
                style={{ width: '100%' }}
              />
            )}
            {grain === 'puja' && (
              <Select
                density="admin"
                placeholder="Which puja"
                value={dimension}
                onChange={setDimension}
                options={(pujas.data?.items ?? []).map((p) => p.name_en)}
                style={{ width: '100%' }}
              />
            )}
          </div>
        </Section>

        <Section title="Window">
          <div style={{ display: 'flex', gap: 4 }}>
            {WINDOWS.map((w) => (
              <Chip
                key={w.label}
                variant="filter"
                label={w.label}
                selected={dates.days === w.days}
                onClick={() => {
                  const w2 = windowDates(w.days);
                  setSearch({ date_from: w2.from, date_to: w2.to });
                }}
              />
            ))}
          </div>
          <div style={{ marginTop: 'var(--sp-sm)' }}>
            {/* A refetch with no key chosen would ask for an empty key list, which the server refuses. */}
            <Button
              size="sm"
              variant="ghost"
              icon="refresh-cw"
              disabled={selected.length === 0}
              onClick={() => void metrics.refetch()}
            >
              Refresh
            </Button>
          </div>
        </Section>
      </div>

      <div style={{ display: 'grid', gap: 'var(--sp-lg)' }}>
        {selected.length === 0 && (
          <Card variant="flat" padding="var(--sp-xxl)">
            <EmptyState
              icon="chart-no-axes-column"
              title="Pick a metric key"
              body="The explorer asks the server for exactly the keys you select, so nothing is fetched until you choose one."
            />
          </Card>
        )}

        {selected.length > 0 && metrics.isPending && <LoadingState message="Reading the rollup…" />}
        {selected.length > 0 && metrics.isError && (
          <ErrorState title="Could not read the metrics" cause={errorMessage(metrics.error)} />
        )}

        {selected.length > 0 && metrics.isSuccess && points.length === 0 && (
          <Card variant="flat" padding="var(--sp-xxl)">
            <EmptyState
              icon="chart-no-axes-column"
              title="Nothing recorded for these keys"
              body="The rollup has no rows for this window at this grain. Widen the window, or check that the nightly job has run."
            />
          </Card>
        )}

        {selected.length > 0 && metrics.isSuccess && points.length > 0 && (
          <>
            {tooMany ? (
              <Card variant="flat" padding="var(--sp-lg)">
                <Kicker>Chart suppressed</Kicker>
                <p style={{ margin: '8px 0 0', fontSize: 'var(--fs-admin-ui)', color: 'var(--c-ink2)' }}>
                  {selected.length} keys on one axis stops being readable. The table below is the
                  record — deselect down to {MAX_CHARTED_KEYS} to see the chart again.
                </p>
              </Card>
            ) : (
              <Section title="Trend" note="One line per key, over the window you picked">
                <div style={{ display: 'grid', gap: 'var(--sp-md)' }}>
                  {selected.map((k) => {
                    const series = dayLabels.map((d) => byKey.get(k)?.get(d) ?? 0);
                    return (
                      <div key={k} style={{ display: 'grid', gap: 4 }}>
                        <span
                          className="mono-num"
                          style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink2)' }}
                        >
                          {k} · {Math.min(...series).toLocaleString('en-IN')}–
                          {Math.max(...series).toLocaleString('en-IN')}
                        </span>
                        {/* `range`, not `zero`: a metric that sits around 130 all week is a flat
                            line anchored at zero, and the shape is the whole reason to draw it.
                            It is a rescale, so the label above carries the actual range — the
                            contract the Sparkline docstring asks a caller to keep. */}
                        <Sparkline points={series} baseline="range" />
                      </div>
                    );
                  })}
                </div>
              </Section>
            )}

            <Section title="The record" note="The chart is the summary; this is the record" pad={false}>
              <DataTable
                density="compact"
                columns={columns}
                rows={dayLabels.map((day) => ({
                  id: day,
                  day,
                  ...Object.fromEntries(
                    selected.map((k) => [k, (byKey.get(k)?.get(day) ?? 0).toLocaleString('en-IN')]),
                  ),
                }))}
                footer={
                  <>
                    <span>
                      {dayLabels.length} days · {selected.length} keys · {grain} grain
                    </span>
                    <span>Every number comes from the nightly rollup, never from raw events</span>
                  </>
                }
              />
            </Section>
          </>
        )}

        <Section
          title="Event health"
          note={`Accepted against rejected at ingestion, ${health.data?.day ?? 'today'} — this is what catches a broken app release within a day`}
          pad={false}
        >
          {health.isPending && <LoadingState message="Reading today's ingestion tally…" />}
          {health.isError && (
            <ErrorState title="Could not read event health" cause={errorMessage(health.error)} />
          )}
          {health.isSuccess && <EventHealth health={health.data} />}
        </Section>
      </div>
    </div>
  );
}
